import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Toast from '@root/components/toast';
import { subscriptionApi } from '@root/api';
import { ILoading } from '@root/types/common';
import Modal from '@root/components/modal';
import native from '@root/native';
import { isInApp } from '@root/shared';

interface IDev {
  deviceId: string;
  deviceName: string;
}

interface IDetail {
  devList: IDev[];
  /**
   * 0-否
   * 1-是
   */
  isSelected: 0 | 1;
  goodsPrice: number;
  maxDevNum: number;
}

const useUpdateDevice = (): {
  selected: string[];
  detail: IDetail;
  loading: number;
  requesting: boolean;
  onSelected: (vs: any[]) => void;
  onUpdatePackage: () => void;
  getDetail: () => void;
  onCloseWebview: () => void;
} => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [loading, setLoading] = useState<ILoading>(0);
  const [detail, setDetail] = useState({} as IDetail);
  const [selected, setSelected] = useState<string[]>([]);
  const [requesting, setRequesting] = useState(false);

  const onCloseWebview = useCallback(() => {
    if (isInApp) {
      native.webView.close();
      return;
    }
    window.location.href = '/';
  }, []);

  // 更新绑定的设备
  const onUpdatePackage = useCallback(() => {
    if (!selected.length || !id) {
      return;
    }

    if (selected.length > detail.maxDevNum) {
      Toast.info(t('service_max_tips'));
      return;
    }

    setRequesting(true);
    subscriptionApi
      .deviceSwitchPackage({
        deviceIds: selected,
        packageId: id,
        type: 2,
      })
      .then(() => {
        setRequesting(false);
        Modal.confirm({
          content: t('operation_successful'),
          cancel: false,
          confirm: t('ok'),
          onConfirm: () => {
            onCloseWebview();
          },
        });
      })
      .catch(() => {
        Toast.info(t('operation_failed'));
        setRequesting(false);
      });
  }, [detail.maxDevNum, id, onCloseWebview, selected, t]);

  const onSelected = useCallback((vs: any[]) => {
    setSelected(vs);
  }, []);

  const getDetail = useCallback(() => {
    if (!id) {
      return;
    }

    setLoading(0);

    subscriptionApi
      .getDowngradeDevList(id)
      .then((data: any) => {
        setDetail(data);
        if (data.devList) {
          setSelected(data.devList.map((i: IDev) => i.deviceId));
        }
        setLoading(1);
      })
      .catch(() => {
        setLoading(2);
      });
  }, [id]);

  useEffect(() => {
    getDetail();
  }, [getDetail]);

  useEffect(() => {
    return () => {
      Toast.destroy();
    };
  }, []);

  return {
    selected,
    detail,
    loading,
    requesting,
    onSelected,
    onUpdatePackage,
    getDetail,
    onCloseWebview,
  };
};

export default useUpdateDevice;
